import { clamp, translate } from '@studio/js-utils/range';
import type { Encoding } from '@studio/js-utils/time';
import { getInfo, Vendor } from '@studio/models/gamepad';
import type { T_Any, T_Maybe, T_Nil, T_Optional } from '@tryiris-ai/js-types';
import {
  isJsonObject,
  isObject,
  parseJson,
  toBoolean,
  toEnum,
  toJson,
  toObjectEntries,
  toString
} from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import { DeadZone, Layouts } from './constants.js';
import type {
  Action,
  Directive,
  Fingerprint,
  Formula,
  Input,
  Layout,
  Profile,
  Profiles
} from './types.js';
import { Console } from './types.js';
import { Command } from '#~/command/index.js';
import type { Directive as Directive_ } from '#~/command/index.js';
import { logger } from '#~/logger/index.js';

/** @internal */
type Profile$Slice = Pick<Profile, 'console'>;

/**
 * If the specified `Directive` is a `GamepadDirective`, pulse the gamepad.
 *
 * @param directive - The `Directive` to be evaluated.
 */
export const buzz = (directive: Directive_): void => {
  const { data } = directive;

  if (isObject(data) && 'gamepadId' in data && typeof data.gamepadId === 'string') {
    pulse(data.gamepadId);
  }
};

/**
 * Create a default gamepad profile.
 *
 * @param gamepadId - The Gamepad ID.
 * @returns The default gamepad profile.
 */
export const createProfile = (gamepadId: Gamepad['id']): Profile => {
  const info = getInfo(gamepadId);

  // Guess the type of console.
  const console = guessConsole(info.vendor);

  return {
    console,
    formula: toFormula(Layouts[console]),
    gamepadId,
    info
  };
};

/**
 * Deserializes gamepad profile data from preference storage.
 *
 * @internal
 */
export const deserialize = (data?: unknown): Profiles => {
  const json = parseJson(data);

  const profiles: Profiles = {};

  if (!isJsonObject(json)) {
    return profiles;
  }

  toObjectEntries(json).forEach(([k, v]) => {
    const gamepadId = toString(k);

    if (!gamepadId || !isJsonObject(v)) {
      return;
    }

    const slice = <T_Optional<Profile$Slice>>v;

    const info = getInfo(gamepadId);
    const console = toConsole(slice.console);

    profiles[gamepadId] = {
      console,
      formula: toFormula(Layouts[console]),
      gamepadId,
      info
    };
  });

  return profiles;
};

/**
 * Determines if two gamepad inputs are equal.
 *
 * @param first - First input.
 * @param second - Second input.
 * @returns `true` if the inputs are equal, `false` otherwise.
 */
export const equalTo = (first: Input, second: Input): boolean =>
  toFingerprint(first) === toFingerprint(second);

/**
 * Evaluates the state of the specified gamepad and returns the array of directives to be
 * dispatched.
 *
 * @param gamepad - Gamepad to evaluate.
 * @param formula - Action mappings.
 * @returns Array of directives to be dispatched or `undefined` if directives were matched.
 */
export const evaluate = (gamepad: Gamepad, formula: Formula): T_Maybe<Directive[]> => {
  const directives: { [key in Action]?: Directive } = {};

  // This code needs to be very fast!  Choose speed over readability.
  const buttonCount = gamepad.buttons.length;
  const axesCount = gamepad.buttons.length;

  let directiveCount = 0;

  // Check all of the buttons.
  for (let ii = 0; ii < buttonCount; ii++) {
    const button = gamepad.buttons[ii];

    if (!button?.pressed) {
      continue;
    }

    const action = find(formula, { button: ii });

    if (action) {
      directives[action] = { command: action, data: { gamepadId: gamepad.id } };

      directiveCount++;
    }
  }

  // Check all of the axes.
  for (let ii = 0; ii < axesCount; ii++) {
    const axis = gamepad.axes[ii];

    // Ignore movement in the joystick dead zone.
    if (!axis || inDeadZone(axis)) {
      continue;
    }

    const action = find(formula, { axis: ii });

    if (action) {
      directives[action] = {
        command: action,
        data: {
          gamepadId: gamepad.id,
          value: translate(Math.abs(axis), [DeadZone, 1], [0, 1]) * Math.sign(axis)
        }
      };

      directiveCount++;
    }
  }

  // Can we bail?
  if (!directiveCount) {
    return undefined;
  }

  // Swap ZOOM_IN and ZOOM_OUT for the PTZ_ZOOM command.
  if (directives[Command.PTZ_ZOOM_IN]) {
    delete directives[Command.PTZ_ZOOM_IN];
    directives[Command.PTZ_ZOOM] = {
      command: Command.PTZ_ZOOM,
      data: { gamepadId: gamepad.id, value: -1 }
    };
  }
  if (directives[Command.PTZ_ZOOM_OUT]) {
    delete directives[Command.PTZ_ZOOM_OUT];
    directives[Command.PTZ_ZOOM] = {
      command: Command.PTZ_ZOOM,
      data: { gamepadId: gamepad.id, value: 1 }
    };
  }

  return Object.values(directives);
};

/**
 * Given a gamepad `Formula` and an `Input`, find the `Action` (if any) that should be dispatched.
 *
 * @param formula - Action mappings.
 * @param input - Gamepad input.
 * @returns Matching `Action` or `undefined` if no match was found.
 */
export function find(formula: Formula, input: Input): T_Maybe<Action> {
  const fingerprint = toFingerprint(input);

  return fingerprint ? formula[fingerprint] : undefined;
}

/**
 * Returns the gamepad object for the specified gamepad ID.
 *
 * @param gamepadId - The gamepad ID.
 * @returns The gamepad object or `undefined` if the gamepad was not found.
 */
export const getGamepad = (gamepadId: Gamepad['id']): T_Maybe<Gamepad> => {
  // We have the need for speed here.
  const gamepads = navigator.getGamepads();

  const gamepadCount = gamepads.length;

  if (!gamepadCount) {
    return undefined;
  }

  for (let ii = 0; ii < gamepadCount; ii++) {
    const gamepad = gamepads[ii];

    if (gamepad && gamepad.id === gamepadId) {
      return gamepad;
    }
  }

  return undefined;
};

/**
 * Guess at the type of game console based on the vendor of the gamepad.
 *
 * @param vendor - The vendor of the gamepad.
 * @returns The type of console for which this gamepad is intended.
 */
export function guessConsole(vendor?: T_Maybe<Vendor>): Console {
  if (vendor === Vendor.Microsoft) {
    return Console.Xbox;
  }

  if (vendor === Vendor.Nintendo) {
    return Console.Switch;
  }

  if (vendor === Vendor.Sony) {
    return Console.PlayStation;
  }

  return Console.Generic;
}

/**
 * Indicates if the specified axis value is in the joystick dead zone.
 *
 * @param axis - Axis value to be evaluated.
 * @returns `true` if the axis value is in the dead zone, `false` otherwise.
 */
export function inDeadZone(axis: number): boolean {
  return Math.abs(axis) <= DeadZone;
}

/**
 * Indicates if the specified button is pressed.
 *
 * @param buttons - Array of gamepad buttons.
 * @param index - Index of the button to be evaluated.
 * @returns `true` if the button is pressed, `false` otherwise.
 */
export const isPressed = (
  buttons: readonly (GamepadButton | boolean)[],
  index: number
): boolean => {
  const button = index >= 0 && index < buttons.length ? buttons[index] : undefined;

  return toBoolean(typeof button === 'object' ? button.pressed : button);
};

/**
 * Pulse the gamepad's vibration motors.
 *
 * @param gamepadId - ID of the gamepad to be pulsed.
 * @param options - Pulse options.
 * @param options.duration - Pulse duration in milliseconds.
 * @param options.magnitude - Pulse magnitude between 0 and 1.
 */
export function pulse(
  gamepadId: Gamepad['id'],
  options: { duration: Encoding.MILLISECONDS; magnitude: number } = {
    duration: 250,
    magnitude: 0.75
  }
): void {
  // Find the specified gamepad.
  const gamepad = getGamepad(gamepadId);

  if (!gamepad) {
    return;
  }

  // No idea why this won't compile.
  type F = (...args: T_Any[]) => Promise<void>;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const shutUp: T_Optional<{
    hapticActuators: F[];
    vibrationActuator: F;
  }> = <T_Any>(<unknown>gamepad);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const actuator:
    | T_Nil
    | T_Optional<{
        playEffect: (...args: unknown[]) => Promise<void>;
        pulse: (...args: unknown[]) => Promise<void>;
      }> = <T_Any>(<unknown>(shutUp.vibrationActuator ?? shutUp.hapticActuators?.[0]));

  if (!actuator) {
    return;
  }

  const magnitude = clamp(options.magnitude, [0, 1]);

  if (actuator.pulse) {
    actuator.pulse(magnitude, options.duration).catch((error) => {
      logger.error(error);
    });
  } else if (actuator.playEffect) {
    actuator
      .playEffect('dual-rumble', {
        duration: options.duration,
        startDelay: 0,
        strongMagnitude: magnitude,
        weakMagnitude: magnitude
      })
      .catch((error: Error) => {
        logger.error(error);
      });
  }
}

/**
 * Serializes gamepad profile data to preference storage.
 *
 * @internal
 */
export const serialize = (profiles: Profiles): string => {
  const entries = toObjectEntries(profiles).map(
    ([gamepadId, profile]): [Gamepad['id'], Profile$Slice] => [
      gamepadId,
      pick(profile, ['console'])
    ]
  );

  return toJson(Object.fromEntries(entries));
};

/**
 * Transforms the specified value to a `Console` enumeration.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Console` enumeration or `Console.Generic`.
 */
export function toConsole(value: unknown): Console {
  return toEnum(Console, value, {
    aliases: {
      [Console.PlayStation]: ['ps']
    },
    default: Console.Generic
  });
}

/**
 * Returns the `Fingerprint` of the specified gamepad input.
 *
 * @param input - Gamepad input.
 * @returns Fingerprint or `undefined` if an unmapped input was specified.
 */
export function toFingerprint(input: Input): T_Maybe<Fingerprint> {
  if ('axis' in input) {
    return `${input.axis}:x`;
  } else if ('button' in input) {
    return `x:${input.button}`;
  }

  return undefined;
}

/**
 * Transforms the specified `Layout` into an `Formula`.
 *
 * @param layout - Gamepad layout to be transformed.
 * @returns Corresponding formula.
 */
export function toFormula(layout: T_Optional<Layout>): Formula {
  const formula: Formula = {};

  toObjectEntries(layout).forEach(([command, inputOrInputs]) => {
    if (!inputOrInputs) {
      return;
    }

    if (Array.isArray(inputOrInputs)) {
      // Multi Input.
      inputOrInputs.forEach((singleInput) => {
        const fingerprint = toFingerprint(singleInput);
        if (fingerprint) {
          formula[fingerprint] = command as Action;
        }
      });
    } else {
      // Single Input.
      const fingerprint = toFingerprint(inputOrInputs as Input);
      if (fingerprint) {
        formula[fingerprint] = command as Action;
      }
    }
  });

  return formula;
}
